/** Spec compiler: validated GameSpec data → an executable program (ordered
 * module instances + per-archetype animation permits + HUD bindings). The
 * compiled program is pure data plus module references; the runtime executes
 * it. Nothing here generates source text. */
import type { JsonObject } from '../json.js';
import type { AnimStateName } from '../animStates.js';
import type { AnyComposeModule, HudFieldDeclV1 } from './module.js';
import { validateGameSpec, archetypeAnimStates, type GameSpecV1 } from './spec.js';

export interface CompiledInstanceV1 {
  /** Global instance key, or the module id for entity modules. */
  key: string;
  module: AnyComposeModule;
  config: JsonObject;
  /** Owning archetype for entity modules; null for globals. */
  archetype: string | null;
  /** Proof-harness stub: declarations kept, behaviour removed. */
  stubbed: boolean;
}

export interface CompiledHudFieldV1 {
  instance: CompiledInstanceV1 | null;
  decl: HudFieldDeclV1;
}

export interface CompiledGameV1 {
  spec: GameSpecV1;
  /** Execution order within each stage. */
  instances: CompiledInstanceV1[];
  archetypeModules: Record<string, CompiledInstanceV1[]>;
  /** archetype → module key → states that module may set on it. */
  animPermits: Record<string, Record<string, AnimStateName[]>>;
  /** archetype → every state it can show (the art slot plan's strip list). */
  animStates: Record<string, AnimStateName[]>;
  /** Every presentation event kind any instance may emit, sorted. */
  events: string[];
  hud: CompiledHudFieldV1[];
}

export class GameSpecError extends Error {
  constructor(readonly errors: string[]) {
    super(`invalid game spec:\n- ${errors.join('\n- ')}`);
    this.name = 'GameSpecError';
  }
}

export interface CompileOptionsV1 {
  /** Module ids whose behaviour is removed (proof "stub check"). */
  stub?: readonly string[];
}

export function compileGameSpec(input: unknown, registry: ReadonlyMap<string, AnyComposeModule>,
  options: CompileOptionsV1 = {}): CompiledGameV1 {
  const validation = validateGameSpec(input, registry);
  if (!validation.ok) throw new GameSpecError(validation.errors);
  const spec = validation.spec;
  const stub = new Set(options.stub ?? []);
  for (const id of stub) if (!registry.has(id)) throw new GameSpecError([`stub: unknown module ${id}`]);
  const instances: CompiledInstanceV1[] = [];
  for (const use of spec.globals) {
    const module = registry.get(use.use)!;
    instances.push({ key: use.key ?? module.id, module, config: use.config, archetype: null, stubbed: stub.has(module.id) });
  }
  const archetypeModules: Record<string, CompiledInstanceV1[]> = {};
  const animPermits: CompiledGameV1['animPermits'] = {};
  const animStates: CompiledGameV1['animStates'] = {};
  for (const [name, archetype] of Object.entries(spec.archetypes)) {
    archetypeModules[name] = archetype.modules.map(use => {
      const module = registry.get(use.use)!;
      const instance: CompiledInstanceV1 = { key: module.id, module, config: use.config, archetype: name, stubbed: stub.has(module.id) };
      instances.push(instance);
      return instance;
    });
    const permits: Record<string, AnimStateName[]> = {};
    for (const instance of archetypeModules[name]!) permits[instance.key] = [...instance.module.animStates(instance.config)];
    for (const instance of instances.filter(i => i.archetype === null)) {
      const grants = instance.module.animStatesForTag?.(instance.config) ?? [];
      const states = grants.filter(g => archetype.tags.includes(g.tag)).flatMap(g => [...g.states]);
      if (states.length) permits[instance.key] = [...new Set(states)];
    }
    animPermits[name] = permits;
    animStates[name] = archetypeAnimStates(spec, name, registry);
  }
  const events = [...new Set(instances.flatMap(i => [...i.module.events]))].sort();
  const hud: CompiledHudFieldV1[] = spec.hud.map(ref => {
    const caption = ref.label === undefined ? {} : { label: ref.label };
    if (ref.from === 'score') return { instance: null, decl: { id: 'score', label: 'SCORE', kind: 'counter', ...caption } };
    const instance = instances.find(i => i.key === ref.from)!;
    return { instance, decl: { ...instance.module.hud!(instance.config).find(f => f.id === ref.field)!, ...caption } };
  });
  return { spec, instances, archetypeModules, animPermits, animStates, events, hud };
}
